import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// A quick multilingual greeting, then straight into the site.
// Skippable at any time — nobody should be stuck waiting on a loader.

const GREETINGS = [
  { text: 'Hello', lang: '' },
  { text: 'خوش آمدید', lang: 'Pakistan' },
  { text: 'Guten Tag', lang: 'Deutschland' },
  { text: "Let's go", lang: '' },
];

const STEP_MS = 420;

const MultilingualLoader = ({ onComplete }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index >= GREETINGS.length - 1) {
      const done = setTimeout(() => onComplete?.(), STEP_MS + 150);
      return () => clearTimeout(done);
    }
    const t = setTimeout(() => setIndex((i) => i + 1), STEP_MS);
    return () => clearTimeout(t);
  }, [index, onComplete]);

  useEffect(() => {
    const skip = (e) => {
      if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
      onComplete?.();
    };
    window.addEventListener('keydown', skip);
    window.addEventListener('click', skip);
    return () => {
      window.removeEventListener('keydown', skip);
      window.removeEventListener('click', skip);
    };
  }, [onComplete]);

  const current = GREETINGS[index];

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-ink-950 bg-grain overflow-hidden">
      <div className="absolute -top-1/4 -left-1/4 w-[60%] h-[60%] bg-mint-500/10 rounded-full blur-3xl animate-drift" />
      <div className="absolute -bottom-1/4 -right-1/4 w-[60%] h-[60%] bg-indigo-500/10 rounded-full blur-3xl animate-drift" />

      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="relative text-center"
        >
          <h1 className="font-display font-bold text-5xl sm:text-7xl text-gradient">
            {current.text}
          </h1>
          {current.lang && (
            <p className="mt-3 text-white/40 tracking-[0.3em] uppercase text-xs font-mono">
              {current.lang}
            </p>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="absolute bottom-10 flex flex-col items-center gap-3">
        <div className="w-40 h-0.5 bg-white/10 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-mint-400 to-indigo-400"
            initial={{ width: '0%' }}
            animate={{ width: `${((index + 1) / GREETINGS.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <button
          onClick={() => onComplete?.()}
          className="text-white/30 hover:text-white/70 text-xs font-mono transition-colors"
        >
          skip →
        </button>
      </div>
    </div>
  );
};

export default MultilingualLoader;
